import { useEffect, useState } from 'react';
import { Outlet, ScrollRestoration, useParams, useLocation } from 'react-router-dom';
import { useRestaurant } from '@/features/restaurant/useRestaurant';
import ThemeProvider from '@/features/restaurant/ThemeProvider';
import NotFoundPage from '@/pages/NotFoundPage';
import ConnectionLostNotice from '@/components/chrome/ConnectionLostNotice';
import SiteUnavailableNotice from '@/components/chrome/SiteUnavailableNotice';
import LicenseExpiredNotice from '@/components/chrome/LicenseExpiredNotice';
import SiteHeader from '@/components/chrome/SiteHeader';
import SiteFooter from '@/components/chrome/SiteFooter';
import GlobalLoader from '@/components/ui/GlobalLoader';
import ErrorToast from '@/components/ui/ErrorToast';
import CartDrawer from '@/features/cart/CartDrawer';
import AccountModal from '@/features/user/AccountModal/AccountModal';
import { AnimeFxProvider } from '@/components/fx';
import SakuraOverlay from '@/components/ui/SakuraOverlay';
import ScrollToTopButton from '@/components/ui/ScrollToTopButton';
import AnnouncementModal from '@/features/announcement/AnnouncementModal';
import { startPreviewBridge } from '@/features/restaurant/previewBridge';
import { useStyleMode } from '@/features/restaurant/useStyleMode';
import { isHexDark } from '@/lib/color';
import { isTransportError } from '@/app/api/loadResilience';
import PetCompanion from '@/features/ambient/PetCompanion';
import TablePanel from '@/features/tableSession/TablePanel';
import WaiterBar from '@/features/waiter/WaiterBar';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectIsInDineIn,
  selectTableSession,
  rehydrateTableSession,
  deactivateTableSession,
} from '@/features/tableSession/tableSessionSlice';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// The two ways resource_api closes the ordering gate on a restaurant that
// exists. Both arrive as a 403 on the public config read and differ only by
// `code`; neither means "no such restaurant".
function orderingBlockedCode(error: unknown): string | null {
  if (!isRecord(error)) return null;
  if (error.status !== 403) return null;
  if (!isRecord(error.data)) return null;
  const code = error.data.code;
  return code === 'LICENSE_EXPIRED' || code === 'ENTITLEMENT_REQUIRED' ? code : null;
}

export default function RestaurantShell() {
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const { identity, config, isLoading, identityError, configError, refetch } = useRestaurant(
    restaurantUuid ?? '',
  );
  const styleMode = useStyleMode();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const [tablePanelOpen, setTablePanelOpen] = useState(false);

  // Dine-in is scoped to the /:restaurantUuid/table/:tableToken URL. The table
  // session is the source of truth for the data (guest token etc.), but the URL
  // decides whether dine-in is ACTIVE: rehydrate it from sessionStorage only on
  // a table route, and deactivate (without erasing storage) everywhere else.
  // This keeps /:restaurantUuid online-only — a lingering session can't turn a
  // from-home order into a dine-in one — while a return to the table URL (incl.
  // refresh of the dine-in checkout) restores it.
  const isInDineIn = useAppSelector(selectIsInDineIn);
  const tableSession = useAppSelector(selectTableSession);
  // Carnet du serveur (table ouverte depuis /serveur) — le meme drapeau que la
  // WaiterBar plus bas. Le QR scanne par le client reste un parcours client.
  const isWaiterNotebook = isInDineIn && tableSession?.waiterMode === true;
  const onTableRoute = !!restaurantUuid && pathname.startsWith(`/${restaurantUuid}/table/`);
  useEffect(() => {
    if (!restaurantUuid) return;
    if (onTableRoute) {
      if (!isInDineIn) dispatch(rehydrateTableSession(restaurantUuid));
    } else if (isInDineIn) {
      dispatch(deactivateTableSession());
    }
  }, [dispatch, restaurantUuid, onTableRoute, isInDineIn]);

  const name = identity?.name;
  const logoUrl = identity?.logoUrl ?? null;
  const faviconUrl = identity?.faviconUrl ?? null;
  const faviconInverseUrl = identity?.faviconInverseUrl ?? null;
  const iconUrl = identity
    ? resolveFaviconSrc({
        faviconUrl,
        faviconInverseUrl,
        logoUrl,
        logoInverseUrl: identity.logoInverseUrl,
        preference: identity.theme.siteCommandeFaviconVariant ?? 'auto',
        autoBackgroundIsDark: isHexDark(identity.theme.cardBackground),
      })
    : null;
  useEffect(() => {
    if (!name || !iconUrl) return;
    const prevTitle = document.title;
    document.title = name;

    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const created = !link;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const prevHref = link.href;
    const prevType = link.type;
    link.type = 'image/png';
    link.href = iconUrl;

    return () => {
      document.title = prevTitle;
      if (created) link!.remove();
      else {
        link!.href = prevHref;
        link!.type = prevType;
      }
    };
  }, [name, iconUrl]);

  // Pause decorative loops (sakura, mascot, marquee) during scroll. On mobile
  // Safari, running multiple infinite compositing-layer animations alongside
  // scroll reflow makes fixed-position chrome (the scroll-top button) judder.
  // Freezing animations while the user is actively scrolling avoids the
  // contention; they resume 200ms after the last scroll event.
  useEffect(() => {
    let timer = 0;
    const onScroll = () => {
      document.body.classList.add('is-scrolling');
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        document.body.classList.remove('is-scrolling');
        timer = 0;
      }, 200);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timer) window.clearTimeout(timer);
      document.body.classList.remove('is-scrolling');
    };
  }, []);

  // Adaptive FPS downgrade — measures real frame rate via RAF over 1.5s
  // windows. After 2 consecutive windows below 45fps we add body.fx-reduced
  // (which hides sakura, mascot, steam etc). After 2 windows back above 55fps
  // we restore decorative motion. Runs on both pointer kinds since mobile now
  // also has motion (sakura lite); a weak phone gets the same auto-downgrade
  // path as a weak desktop.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const SAMPLE_MS = 1500;
    const DOWNGRADE_FPS = 45;
    const UPGRADE_FPS = 55;
    const STREAK = 2;

    let raf = 0;
    let windowStart = 0;
    let frames = 0;
    let lowStreak = 0;
    let highStreak = 0;
    let reduced = false;

    const tick = (t: number) => {
      if (document.hidden) {
        // Reset measurement on tab hide — RAF is throttled when hidden,
        // which would otherwise look like a sustained low-FPS event.
        windowStart = 0;
        frames = 0;
      } else {
        if (!windowStart) {
          windowStart = t;
          frames = 0;
        }
        frames += 1;

        if (t - windowStart >= SAMPLE_MS) {
          const fps = (frames * 1000) / (t - windowStart);
          if (fps < DOWNGRADE_FPS) {
            lowStreak += 1;
            highStreak = 0;
            if (!reduced && lowStreak >= STREAK) {
              document.body.classList.add('fx-reduced');
              reduced = true;
              lowStreak = 0;
            }
          } else if (fps > UPGRADE_FPS) {
            highStreak += 1;
            lowStreak = 0;
            if (reduced && highStreak >= STREAK) {
              document.body.classList.remove('fx-reduced');
              reduced = false;
              highStreak = 0;
            }
          } else {
            lowStreak = 0;
            highStreak = 0;
          }
          windowStart = t;
          frames = 0;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      document.body.classList.remove('fx-reduced');
    };
  }, []);

  useEffect(() => {
    if (!identity?.theme) return;
    return startPreviewBridge(identity.theme);
  }, [identity?.theme]);

  if (!restaurantUuid) return <NotFoundPage />;
  if (isLoading) return <GlobalLoader />;
  // DF-0257 — même règle que le carnet serveur : une panne de transport garde
  // son propre écran, sinon un réseau qui cale devient « lien invalide » pour
  // le convive assis à la table.
  if (isTransportError(identityError) || isTransportError(configError)) {
    return <ConnectionLostNotice onRetry={refetch} />;
  }
  if (identityError || !identity) return <NotFoundPage />;
  if (!identity.onlineOrderingFeatureEnabled) return <SiteUnavailableNotice />;
  // ENTITLEMENT_REQUIRED used to fall through to the 404 below, so a
  // restaurant whose online ordering was never activated was indistinguishable
  // from a mistyped link — for the guest AND for whoever they complained to.
  // The identity call above already succeeded, so this restaurant exists.
  if (orderingBlockedCode(configError) === 'LICENSE_EXPIRED') return <LicenseExpiredNotice />;
  if (orderingBlockedCode(configError) === 'ENTITLEMENT_REQUIRED') return <SiteUnavailableNotice />;
  if (configError || !config) return <NotFoundPage />;

  return (
    <AnimeFxProvider staffMode={isWaiterNotebook}>
      <ThemeProvider theme={identity.theme}>
        <div className="min-h-[100dvh] bg-cream flex flex-col">
          {/* Spec §3.1: waiter handheld mode collapses the whole dine-in chrome
              into ONE sticky WaiterBar ("← Tables · Table N · covers · total ·
              FR · 🛒 · 🔒"), shown only for sessions opened from
              /:restaurantUuid/serveur. It replaces SiteHeader here (its language
              + cart moved into the bar), so the two are mutually exclusive. A
              scanned guest QR keeps the normal SiteHeader. */}
          {isWaiterNotebook && tableSession ? (
            <WaiterBar
              restaurantUuid={restaurantUuid}
              tableToken={tableSession.tableToken}
              tableNumber={tableSession.tableNumber}
              onOpenTablePanel={() => setTablePanelOpen(true)}
            />
          ) : (
            <SiteHeader
              identity={identity}
              config={config}
              onOpenTablePanel={() => setTablePanelOpen(true)}
            />
          )}
          <main className="flex-1">
            <Outlet />
          </main>
          {/* Scan-to-order strips the marketing footer: the guest is seated, so
              hours / address / social links are noise. */}
          {!isInDineIn && <SiteFooter identity={identity} config={config} />}
          <ScrollRestoration />
          <CartDrawer />
          <TablePanel open={tablePanelOpen} onClose={() => setTablePanelOpen(false)} />
          {/* Dine-in keeps only the ordering essentials above; account modal and
              all decorative chrome (sakura, mascot, announcement) are stripped. */}
          {!isInDineIn && <AccountModal />}
          <GlobalLoader />
          <ErrorToast />
          {/* SC-1091 — jamais sur la carte de fidélité : un pétale sur un QR le rend
              illisible pour le scanner du comptoir. */}
          {!isInDineIn && styleMode !== 'premium' && !/\/account\/loyalty\/?$/.test(pathname) && <SakuraOverlay />}
          {/* Le bouton flottant « haut de page » se pose sur le « + » de la
              derniere ligne du carnet, et la barre de panier occupe deja ce
              coin. Le serveur a sa barre collee en haut pour revenir. */}
          {!isWaiterNotebook && <ScrollToTopButton />}
          {!isInDineIn && config.announcement && (
            <AnnouncementModal announcement={config.announcement} logoUrl={logoUrl} />
          )}
          {!isInDineIn && styleMode !== 'premium' && <PetCompanion audio={config.ambient} />}
        </div>
      </ThemeProvider>
    </AnimeFxProvider>
  );
}

function resolveFaviconSrc({
  faviconUrl,
  faviconInverseUrl,
  logoUrl,
  logoInverseUrl,
  preference,
  autoBackgroundIsDark,
}: {
  faviconUrl: string | null;
  faviconInverseUrl?: string | null;
  logoUrl: string | null;
  logoInverseUrl?: string | null;
  preference: 'auto' | 'light' | 'dark';
  autoBackgroundIsDark: boolean;
}) {
  const useInverse = preference === 'dark' || (preference === 'auto' && autoBackgroundIsDark);
  return useInverse
    ? (faviconInverseUrl ?? faviconUrl ?? logoInverseUrl ?? logoUrl)
    : (faviconUrl ?? faviconInverseUrl ?? logoUrl ?? logoInverseUrl ?? null);
}
